import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import arrowLeft from '~/assets/icons/arrow_left_2.svg';
import reset from '~/assets/icons/reset.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import {
	PropertyContainer,
	PropertyContainerRow,
} from '~/components/ui/PropertyContainer';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { Slider } from '~/components/ui/Slider';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	HandlingMode,
	InputAction,
	TechTuningCategory,
	TechTuningSection,
} from '~/shared';
import { useModal } from '../../hooks';
import {
	setActiveCategory,
	setActiveStage,
	setInitialHandling,
	setTabIndex,
	setUnsavedChanges,
	showSaveModal,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { SectionLock } from '../SectionLock';
import {
	handlingIcons,
	modeList,
	techTuningSectionName,
} from './SettingsTab.model';
import {
	StyledButtonContent,
	StyledButtonsContainer,
	StyledContainer,
	StyledPopupContainer,
	StyledPresetsContainer,
	StyledResetButton,
	StyledSectionBody,
	StyledSettingsCategoryHead,
	StyledSettingsCategoryName,
} from './SettingsTab.styled';

const SettingsTab = () => {
	const dispatch = useLayoutDispatch();
	const mode = useObservable(rage.vehicles.getLocalVehicleHandlingMode());
	const handling = useObservable(rage.handlingTuning.getHandling());
	const handlingTuning = useObservable(rage.handlingTuning.getParameters());
	const modalState = useObservable(rage.modal.getCurrentModal());
	const [popupVisible, setPopupVisible] = useState<boolean>(false);
	const sections = useObservable(rage.handlingTuning.getSections());
	const allSections = useObservable(rage.handlingTuning.getAllSections());
	const unsavedChanges = useLayoutSelector(
		(state) => state.handling.unsavedChanges,
	);
	const initialHandling = useLayoutSelector(
		(state) => state.handling.initialHandling,
	);
	const prevModeRef = useRef<HandlingMode>(mode);
	const {
		handleSetupNewPresetModal,
		handleResetAllModal,
		handleResetModal,
		handleUnsavedChangesModeModal,
		handleUnsavedChangesExitModal,
		handleUnsavedChangesLeaveModal,
	} = useModal();

	const handleSetMode = useCallback(
		(mode: HandlingMode) => {
			handleUnsavedChangesModeModal(mode);
		},
		[handleUnsavedChangesModeModal],
	);

	// @TODO: Тест-драйв хендлингов
	// const handleTestDrive = useCallback(async () => {
	// 	rage.handlingTuning.setHandling(handling);
	// 	await rage.handlingTuning.testDrive();
	// }, [handling]);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (modalState) {
			return;
		}

		handleSetupNewPresetModal();
	});

	useInputAction(InputAction.HandlingSave, () => {
		if (modalState) {
			return;
		}

		dispatch(showSaveModal());
	});

	useInputAction(InputAction.HandlingResetAll, () => {
		if (modalState) {
			return;
		}

		handleResetAllModal();
	});

	useEffect(() => {
		if (Object.keys(handling).length > 0 && prevModeRef.current !== mode) {
			dispatch(setInitialHandling({ ...handling }));
			prevModeRef.current = mode;
		} else if (
			Object.keys(handling).length > 0 &&
			Object.keys(initialHandling).length === 0
		) {
			dispatch(setInitialHandling({ ...handling }));
		}
	}, [mode, handling, initialHandling, dispatch]);

	const checkIfHandlingChanged = useCallback(
		(newHandling: Record<string, any>) => {
			if (Object.keys(initialHandling).length === 0) {
				return false;
			}

			const handlingProperties = handlingTuning
				.filter((e) => typeof newHandling[e.property] === 'number')
				.map((e) => e.property);

			for (const property of handlingProperties) {
				if (newHandling[property] !== initialHandling[property]) {
					return true;
				}
			}

			return false;
		},
		[initialHandling, handlingTuning],
	);

	useEffect(() => {
		if (
			!unsavedChanges &&
			Object.keys(handling).length > 0 &&
			Object.keys(initialHandling).length > 0
		) {
			const hasChanges = checkIfHandlingChanged(handling);
			if (hasChanges) {
				dispatch(setInitialHandling({ ...handling }));
			}
		}
	}, [unsavedChanges, handling, initialHandling, checkIfHandlingChanged]);

	useEffect(() => {
		if (
			Object.keys(initialHandling).length === 0 ||
			Object.keys(handling).length === 0
		) {
			return;
		}

		const hasChanges = checkIfHandlingChanged(handling);
		dispatch(setUnsavedChanges(hasChanges));
	}, [handling, initialHandling, checkIfHandlingChanged]);

	const handlingSections = useMemo(() => {
		const configured: {
			id: TechTuningSection;
			requiredCategory: TechTuningCategory | null;
		}[] = allSections.length
			? allSections
			: sections.map((id) => ({ id, requiredCategory: null }));

		return configured
			.filter(
				(section) =>
					!sections.includes(section.id) ||
					handlingTuning.some(
						(e) =>
							e.section === section.id &&
							typeof handling[e.property] === 'number',
					),
			)
			.map((section) => ({
				id: section.id,
				name: techTuningSectionName[section.id],
				requiredCategory: section.requiredCategory,
				locked: !sections.includes(section.id),
			}));
	}, [allSections, sections, handlingTuning, handling]);

	const handleBuyPart = useCallback(
		(category: TechTuningCategory) => {
			handleUnsavedChangesLeaveModal(() => {
				dispatch(setTabIndex(0));
				dispatch(setActiveCategory(category));
				dispatch(setActiveStage(StageTypes.List));
			});
		},
		[handleUnsavedChangesLeaveModal],
	);

	return (
		<StyledContainer>
			<CSSTransition
				in={popupVisible}
				timeout={300}
				classNames={'popup'}
				unmountOnExit
			>
				<StyledPopupContainer>
					<Text>Сбросить все</Text>
				</StyledPopupContainer>
			</CSSTransition>
			<StyledPresetsContainer>
				{modeList.map((item, index) => (
					<Button
						height={ButtonHeight.Height40}
						color={
							mode === item.name ? ButtonColors.Select : ButtonColors.Default
						}
						onClick={() => handleSetMode(item.name)}
						key={index}
					>
						<Text>{item.title}</Text>
					</Button>
				))}
				<Button
					height={ButtonHeight.Height40}
					color={ButtonColors.Default}
					onClick={handleResetAllModal}
					onMouseEnter={() => setPopupVisible(true)}
					onMouseLeave={() => setPopupVisible(false)}
					style={{ marginLeft: 10 }}
					square
				>
					<img src={reset} />
				</Button>
			</StyledPresetsContainer>
			<ScrollContainer
				height={473}
				width={380}
				showMask
				style={{ marginTop: 30 }}
			>
				{handlingSections.map((section, index) => (
					<Fragment key={section.id}>
						<StyledSettingsCategoryHead
							style={{ marginTop: index > 0 ? 30 : 0 }}
						>
							<StyledSettingsCategoryName>
								<img src={handlingIcons[section.id]} />
								<Text>{section.name}</Text>
							</StyledSettingsCategoryName>
						</StyledSettingsCategoryHead>
						<StyledSectionBody $locked={section.locked}>
							<PropertyContainer>
								{handlingTuning
									.filter(
										(e) =>
											e.section === section.id &&
											typeof handling[e.property] === 'number',
									)
									.map((property, index, arr) => {
										const propIndex = handlingTuning.findIndex(
											(e) => e.property === property.property,
										);
										return (
											<Fragment key={`prop#${propIndex}`}>
												<PropertyContainerRow>
													<Text>{property.name}</Text>
													<StyledResetButton
														onClick={() => {
															handleResetModal(
																property.name,
																property.property,
															);
															playSound(Sound.ClickButton);
														}}
														onMouseEnter={() => playSound(Sound.LinkCursor)}
													>
														Сбросить
													</StyledResetButton>
												</PropertyContainerRow>
												<PropertyContainerRow>
													<Slider
														min={property.limits[0]}
														max={property.limits[1]}
														step={property.step}
														value={handling[property.property] as number}
														width={280}
														onChange={(newValue) => {
															const newHandling = {
																...handling,
																[property.property]: newValue,
															};
															const hasChanges =
																checkIfHandlingChanged(newHandling);
															dispatch(setUnsavedChanges(hasChanges));
															rage.handlingTuning.setHandling(newHandling);
														}}
														valueInput
														center
													/>
												</PropertyContainerRow>
												{index < arr.length - 1 && (
													<Divider $direction={'H'} style={{ opacity: 0.1 }} />
												)}
											</Fragment>
										);
									})}
							</PropertyContainer>
							{section.locked && (
								<SectionLock
									section={section.id}
									requiredCategory={section.requiredCategory}
									onBuy={handleBuyPart}
								/>
							)}
						</StyledSectionBody>
					</Fragment>
				))}
			</ScrollContainer>
			{/* <Button
				color={ButtonColors.Default}
				height={ButtonHeight.Height50}
				onClick={handleTestDrive}
				style={{ marginTop: 30 }}
			>
				<StyledButtonContent style={{ gap: 10 }}>
					<img src={swheel} />
					<Text>Тест-драйв</Text>
				</StyledButtonContent>
			</Button> */}
			<StyledButtonsContainer>
				<Button
					height={ButtonHeight.Height50}
					color={ButtonColors.Default}
					style={{ fontSize: 16 }}
					onClick={handleUnsavedChangesExitModal}
				>
					<StyledButtonContent>
						<img src={arrowLeft} />
						<Text>Вернуться</Text>
					</StyledButtonContent>
				</Button>
				<Button
					height={ButtonHeight.Height50}
					color={ButtonColors.Select}
					style={{ fontSize: 16 }}
					onClick={handleSetupNewPresetModal}
				>
					<Text>Применить</Text>
				</Button>
			</StyledButtonsContainer>
		</StyledContainer>
	);
};

export { SettingsTab };
